import { motion } from "framer-motion";
import { Award, Building2, GraduationCap, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { COLLEGE, IMAGES } from "../../lib/constants";

const points = [
  { icon: GraduationCap, title: "Women-First Engineering", desc: "A campus uniquely designed for women to lead in tech and engineering." },
  { icon: Award, title: "Autonomous & NAAC 'A'", desc: "UGC-recognized autonomous status with the prestigious NAAC 'A' grade." },
  { icon: Building2, title: "Industry-Oriented", desc: "Curriculum co-designed with industry; live projects every semester." },
  { icon: Sparkles, title: "Innovation & Skills", desc: "R&D labs, hackathons and certifications integrated into every program." },
];

const About = () => {
  return (
    <section id="about" className="py-20 md:py-28 bg-white" data-testid="section-about">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="relative"
        >
          <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-blue-900/15">
            <img src={IMAGES.about} alt="Megha Institute campus" className="w-full h-[460px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-megha-primary/40 via-transparent to-transparent" />
          </div>

          {/* Floating package card */}
          <div className="absolute -bottom-6 -right-2 md:-right-6 bg-white rounded-2xl p-5 shadow-2xl shadow-blue-900/15 border border-slate-100 max-w-[240px]" data-testid="about-package-card">
            <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-megha-accent">Top Placement</div>
            <div className="text-3xl font-heading font-black text-megha-text mt-1">{COLLEGE.highestPackage}</div>
            <div className="text-xs text-slate-500 mt-1">Highest package secured by an MIETW student in 2025.</div>
          </div>

          {/* Floating accreditation pill */}
          <div className="absolute -top-5 left-5 bg-megha-highlight px-4 py-2 rounded-full text-xs font-extrabold tracking-wider uppercase text-megha-text shadow-lg shadow-yellow-500/30">
            NAAC 'A' Grade
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-megha-accent">About MIETW</div>
          <h2 className="mt-2 font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-tight text-megha-text">
            Engineering excellence, <span className="text-megha-accent">built for women.</span>
          </h2>
          <p className="mt-5 text-slate-600 text-base md:text-lg leading-relaxed">
            {COLLEGE.name} is a UGC-recognized autonomous institution in {COLLEGE.location}, dedicated
            to empowering women through technical education. With NAAC 'A' grade accreditation, modern
            laboratories, industry partnerships and a placement record peaking at {COLLEGE.highestPackage}, we
            prepare engineers for the future — not just the next exam.
          </p>

          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {points.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="bg-slate-50 rounded-2xl p-4 border border-slate-100 hover:border-megha-accent/30 hover:bg-white hover:shadow-md transition-all"
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-megha-primary to-megha-accent flex items-center justify-center text-white">
                  <p.icon className="w-5 h-5" />
                </div>
                <div className="font-heading font-bold text-megha-text mt-3 text-base">{p.title}</div>
                <div className="text-sm text-slate-500 mt-1">{p.desc}</div>
              </motion.div>
            ))}
          </div>

          <a href="#programs">
            <Button
              data-testid="btn-about-explore"
              variant="outline"
              className="mt-8 h-12 rounded-full border-2 border-megha-primary text-megha-primary hover:bg-megha-primary hover:text-white font-bold px-6 transition-all"
            >
              Explore B.Tech Programs
            </Button>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
